import { IInterval, ISendEmailFeatures } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../Checkbox';
import { Modal } from '../../Modal';
import { IntervalDropdowns } from '../IntervalDropdowns';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	isOpen?: boolean;
	isSaving?: boolean;
	onRequestClose?(sendEmailFeatures?: Partial<ISendEmailFeatures>): void;
	sendEmailFeatures?: ISendEmailFeatures;
	styles?: StyleDeclarationValue[];
}

export const BusinessHoursModal: React.FC<IProps> = ({
	className,
	isOpen,
	isSaving,
	onRequestClose,
	sendEmailFeatures,
}) => {
	const [state, setState] = React.useState<ISendEmailFeatures>(sendEmailFeatures);

	const {
		mondayInterval,
		tuesdayInterval,
		wednesdayInterval,
		thursdayInterval,
		fridayInterval,
		saturdayInterval,
		sundayInterval,
		weekdaysInterval,
		useDiscreteIntervals,
	} = state;

	const onSelectionChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setState(prev => ({ ...prev, useDiscreteIntervals: e.target.checked }));
	}, []);

	const onSave = () => {
		const sendEmailFeaturesData = {
			...state,
			observeSendIntervals: true, // Always keep business hours enabled when saving from this modal
		};

		onRequestClose?.(sendEmailFeaturesData);
	};

	const handleRequestClose = () => {
		onRequestClose?.();
	};

	const onUpdateWeekdays = React.useCallback((updatedWeekdaysInterval: IInterval) => {
		if (updatedWeekdaysInterval && updatedWeekdaysInterval.startMinutes < updatedWeekdaysInterval.endMinutes) {
			setState(prev => ({ ...prev, weekdaysInterval: updatedWeekdaysInterval }));
		} else {
			toast.error('End time must be greater than start time.');
		}
	}, []);

	const onUpdateInterval = React.useCallback((key: keyof ISendEmailFeatures, interval: IInterval) => {
		if (!interval || (interval && interval.startMinutes < interval.endMinutes)) {
			setState(prev => ({ ...prev, [key]: interval }));
		} else {
			toast.error('End time must be greater than start time.');
		}
	}, []);

	const updateMondayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('mondayInterval', interval),
		[onUpdateInterval]
	);
	const updateTuesdayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('tuesdayInterval', interval),
		[onUpdateInterval]
	);
	const updateWednesdayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('wednesdayInterval', interval),
		[onUpdateInterval]
	);
	const updateThursdayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('thursdayInterval', interval),
		[onUpdateInterval]
	);
	const updateFridayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('fridayInterval', interval),
		[onUpdateInterval]
	);
	const updateSaturdayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('saturdayInterval', interval),
		[onUpdateInterval]
	);
	const updateSundayInterval = React.useCallback(
		(interval: IInterval) => onUpdateInterval('sundayInterval', interval),
		[onUpdateInterval]
	);

	return (
		<Modal className={`${className || ''}`} isOpen={isOpen} onRequestClose={handleRequestClose} useDefaultHeader={true}>
			<div>Manage Business Hours</div>
			<div className={css(styleSheet.header)}>
				<Checkbox checked={useDiscreteIntervals} onChange={onSelectionChanged}>
					<div>Set individual business hours for each day of the week</div>
				</Checkbox>
			</div>
			<div>
				{useDiscreteIntervals ? (
					<>
						<div className={css(styleSheet.header)}>Monday</div>
						<IntervalDropdowns
							canRemove={true}
							disabled={isSaving}
							interval={mondayInterval}
							isFriday={false}
							onUpdateInterval={updateMondayInterval}
						/>
						<div className={css(styleSheet.header)}>Tuesday</div>
						<IntervalDropdowns
							canRemove={true}
							disabled={isSaving}
							interval={tuesdayInterval}
							isFriday={false}
							onUpdateInterval={updateTuesdayInterval}
						/>
						<div className={css(styleSheet.header)}>Wednesday</div>
						<IntervalDropdowns
							canRemove={true}
							disabled={isSaving}
							interval={wednesdayInterval}
							isFriday={false}
							onUpdateInterval={updateWednesdayInterval}
						/>
						<div className={css(styleSheet.header)}>Thursday</div>
						<IntervalDropdowns
							canRemove={true}
							disabled={isSaving}
							interval={thursdayInterval}
							isFriday={false}
							onUpdateInterval={updateThursdayInterval}
						/>
					</>
				) : (
					<>
						<div className={css(styleSheet.header)}>Weekdays (Monday - Thursday)</div>
						<IntervalDropdowns disabled={isSaving} interval={weekdaysInterval} onUpdateInterval={onUpdateWeekdays} />
					</>
				)}
				<div className={css(styleSheet.header)}>Friday</div>
				<IntervalDropdowns
					canRemove={true}
					disabled={isSaving}
					interval={fridayInterval}
					isFriday={!useDiscreteIntervals}
					onUpdateInterval={updateFridayInterval}
				/>
				<div className={css(styleSheet.header)}>Saturday</div>
				<IntervalDropdowns
					canRemove={true}
					disabled={isSaving}
					interval={saturdayInterval}
					onUpdateInterval={updateSaturdayInterval}
				/>
				<div className={css(styleSheet.header)}>Sunday</div>
				<IntervalDropdowns
					canRemove={true}
					disabled={isSaving}
					interval={sundayInterval}
					onUpdateInterval={updateSundayInterval}
				/>
			</div>
			<Button variant='primary' size='sm' onClick={onSave} style={{ marginTop: 16 }}>
				Save
			</Button>
		</Modal>
	);
};
